<template>
  <div :class="ns.b()">
    <component
      :is="FormCreateComponent"
      :model-value="formData"
      :rule="rule"
      :option="mergedOption"
      :disabled="disabled"
      :preview="preview"
      @update:model-value="onUpdateModel"
      @update:api="onUpdateApi"
    />
  </div>
</template>

<script lang="ts" setup>
import { getCurrentInstance, markRaw, reactive, ref, watch } from 'vue'
import { useNamespace } from '@zoryn/hooks'
import { bindZorynElAliases } from './bind-components'
import { FormCreateComponent, registerField, zFormCreate } from './engine'
import { formCreateProps } from './form-create'
import { normalizeFormCreateOption } from './option'

import type { FormCreateApi, FormCreateOption } from './types'

defineOptions({
  name: 'ZFormCreate',
})

const props = defineProps(formCreateProps)
const emit = defineEmits<{
  'update:modelValue': [value: Record<string, unknown>]
  'update:api': [value: FormCreateApi]
  submit: [value: Record<string, unknown>]
  reset: [value: Record<string, unknown>]
  fail: [error: unknown]
}>()

const ns = useNamespace('form-create')
const instance = getCurrentInstance()
if (instance?.appContext.app) {
  bindZorynElAliases(instance.appContext.app)
}

const formData = reactive<Record<string, unknown>>({
  ...(props.modelValue ?? {}),
})
const innerApi = ref<FormCreateApi>()
const mergedOption = markRaw<Record<string, unknown>>({})

if (props.components) {
  for (const [type, definition] of Object.entries(props.components)) {
    registerField(type, definition as { component: object })
  }
}

function applyOption(option: FormCreateOption | undefined) {
  const normalized = normalizeFormCreateOption(option) as FormCreateOption & {
    onSubmit?: (formData: Record<string, unknown>, api: FormCreateApi) => void
    onReset?: (formData: Record<string, unknown>, api: FormCreateApi) => void
  }
  Object.assign(mergedOption, normalized, {
    onSubmit(data: Record<string, unknown>, api: FormCreateApi) {
      emit('submit', data)
      normalized.onSubmit?.(data, api)
    },
    onReset(data: Record<string, unknown>, api: FormCreateApi) {
      emit('reset', data)
      normalized.onReset?.(data, api)
    },
  })
}

applyOption(props.option)

watch(
  () => props.option,
  (option) => applyOption(option)
)

function onUpdateModel(_value: Record<string, unknown>) {
  emit('update:modelValue', formData)
}

function onUpdateApi(api: FormCreateApi) {
  if (api === innerApi.value) return
  innerApi.value = api
  emit('update:api', api)
}

watch(
  () => props.modelValue,
  (value) => {
    if (!value || value === formData) return
    Object.assign(formData, value)
  }
)

defineExpose({
  get api() {
    return innerApi.value
  },
  formData: () => innerApi.value?.formData?.() ?? { ...formData },
  getValue: (field: string) => innerApi.value?.getValue?.(field),
  setValue: (field: string | Record<string, unknown>, value?: unknown) => {
    if (!innerApi.value) return
    if (typeof field === 'string') {
      innerApi.value.setValue(field, value)
    } else {
      innerApi.value.setValue(field)
    }
    emit('update:modelValue', formData)
  },
  validate: () => innerApi.value?.validate?.(),
  validateField: (field: string) => innerApi.value?.validateField?.(field),
  submit: () => innerApi.value?.submit?.(),
  resetFields: () => innerApi.value?.resetFields?.(),
  hidden: (field: string, hidden = true) =>
    innerApi.value?.hidden?.(hidden, field),
  disabled: (field: string, disabled = true) =>
    innerApi.value?.disabled?.(disabled, field),
  getRule: (field?: string) =>
    field ? innerApi.value?.getRule?.(field) : innerApi.value?.rule,
  updateRule: (field: string, rule: object) =>
    innerApi.value?.updateRule?.(field, rule),
  append: (rule: object, after?: string) => {
    if (after) {
      innerApi.value?.append?.(rule, after)
      return
    }
    innerApi.value?.append?.(rule)
  },
  remove: (field: string) => innerApi.value?.removeField?.(field),
  engine: zFormCreate,
})
</script>
